import { forwardRef, InputHTMLAttributes, useRef } from 'react';

import { useId } from 'react-id-generator';
import { useMediaQuery } from 'react-responsive';
export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  labelClassname?: string;
  required?: boolean;
  scrollOnFocus?: boolean;
}

//* 고정 덮개(내비·탭 바) 아래로 입력칸을 띄우는 여백
const COVER_GAP = 56;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      required,
      labelClassname,
      className,
      children,
      autoComplete = 'off',
      scrollOnFocus = false,
      ...props
    },
    ref,
  ) => {
    const [id] = useId(1, 'textfield');
    const inputRef = useRef<HTMLInputElement | null>(null);

    const isMobile = !useMediaQuery({
      query: '(min-width: 768px)',
    });

    const handleClick = () => {
      const containerHeight = window.innerHeight;
      //* 화면 맨 위를 덮는 fixed/sticky 요소(상단 내비 + 수정 페이지의 탭 바 등)의 아래 끝을 실측.
      //* 80px 상수로 맞추면 탭 바가 하나 더 있는 고인정보 수정에서 입력칸이 그 밑으로 숨음(2026-11-02 실사고)
      const topCoverBottom = () => {
        if (typeof document.elementsFromPoint !== 'function') return 0;
        const probeX = Math.floor(window.innerWidth / 2);
        return [1, 40, 90].reduce((max, y) => {
          const bottoms = document.elementsFromPoint(probeX, y).map((el) => {
            const position = window.getComputedStyle(el).position;
            if (position !== 'fixed' && position !== 'sticky') return 0;
            const r = el.getBoundingClientRect();
            return r.top <= y && r.bottom > y && r.height < containerHeight / 2
              ? r.bottom
              : 0;
          });
          return Math.max(max, ...bottoms);
        }, 0);
      };
      const contentHeight = document.documentElement.scrollHeight;
      const maxScrollY = contentHeight - containerHeight;

      if (inputRef.current && scrollOnFocus) {
        // 모바일 환경에서는 스크롤 동작을 조정
        if (isMobile) {
          // 요소의 위치 계산
          const rect = inputRef.current.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;

          // 덮개(내비·탭 바) 아래 한 칸(56px) 띄워서, 덮개가 없으면 기존처럼 80px. 24px는 붙어 보여 늘림(2026-11-02)
          const targetScrollY = elementTop - Math.max(80, topCoverBottom() + COVER_GAP);

          // 부드러운 스크롤 적용
          window.scrollTo({
            top: targetScrollY,
            behavior: 'smooth',
          });

          return;
        }

        const currentScrollY = window.scrollY;
        const inputTop = inputRef.current.getBoundingClientRect().top;
        const inputTopWithScroll = Math.max(
          0,
          inputTop + currentScrollY - Math.max(180, topCoverBottom() + COVER_GAP),
        );

        const handleResize = () => {
          inputRef.current?.scrollIntoView({ behavior: 'smooth' });
        };

        const animateScroll = (timestamp: number) => {
          if (timestamp < start + duration) {
            const progress = (timestamp - start) / duration;
            const scrollTop = Math.round(
              inputTopWithScroll * progress + startScrollY * (1 - progress),
            );
            window.scrollTo(0, scrollTop);
            if (maxScrollY > currentScrollY || inputTopWithScroll < currentScrollY) {
              requestAnimationFrame(animateScroll);
            }
          } else {
            window.removeEventListener('resize', handleResize);
          }
        };

        window.addEventListener('resize', handleResize);

        const duration = 250;
        const start = performance.now();
        const startScrollY = window.scrollY;

        requestAnimationFrame(animateScroll);
      }
    };

    return (
      <div className="label-col">
        {label && <p className={`${labelClassname} label`}>{label}</p>}
        <div className="relative">
          <input
            autoComplete={autoComplete}
            {...props}
            ref={(el) => {
              inputRef.current = el;
              if (typeof ref === 'function') {
                ref(el);
              } else if (ref !== null) {
                ref.current = el;
              }
            }}
            id={id}
            onClick={handleClick}
            className={`${className} ${
              required ? 'border-warn' : 'theme-border-1'
            } textfield theme-text-1 theme-bg-1 placeholder:theme-text-6 `}
          />
          {children}
        </div>
        {/* {helper && <p className="text-sm text-error">{helper}</p>} */}
      </div>
    );
  },
);
